import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { readString } from '@/lib/pulse-ai/voice-catalog';
import { isPersona, type Persona } from '@/lib/pulse-ai/voice-personas';
import { getSupabaseFunctionError } from '@/lib/supabase-utils';

export const ORG_PROFILE_QUERY_KEY = 'org-profile';

/**
 * Client mirror of `_shared/voice/org-profile.ts` `OrgProfile`. The org identity
 * that genericises the base draft prompt (`organizations.settings.org_profile`).
 * Edited only through the `set-org-profile` edge function (the sole writer).
 */
export interface OrgProfile {
  voicePersona?: Persona;
  organizationType?: string;
  organizationName?: string;
  jurisdiction?: string;
  /** Free-text role: who the office writes as. Feeds the base prompt's role block. */
  roleDescription?: string;
  /** Present only for `corporate`; absent reads as `government`. */
  officeType?: OfficeType;
}

export type OfficeType = 'government' | 'corporate';

/** Coerce the untyped `settings.org_profile` JSON into a typed {@link OrgProfile}. */
export function parseClientOrgProfile(raw: unknown): OrgProfile | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const profile: OrgProfile = {
    voicePersona: isPersona(r.voicePersona) ? r.voicePersona : undefined,
    organizationType: readString(r.organizationType),
    organizationName: readString(r.organizationName),
    jurisdiction: readString(r.jurisdiction),
    roleDescription: readString(r.roleDescription),
    officeType: r.officeType === 'corporate' ? 'corporate' : undefined,
  };
  return Object.values(profile).some((v) => v !== undefined) ? profile : null;
}

/** Read the org identity profile from `organizations.settings.org_profile`. */
export function useOrgProfile(organizationId?: string | null) {
  return useQuery({
    queryKey: [ORG_PROFILE_QUERY_KEY, organizationId],
    queryFn: async (): Promise<OrgProfile | null> => {
      if (!organizationId) {
        return null;
      }
      const { data, error } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', organizationId)
        .maybeSingle();
      if (error) {
        throw error;
      }
      const settings = data?.settings;
      const raw =
        settings && typeof settings === 'object' && !Array.isArray(settings)
          ? (settings as Record<string, unknown>).org_profile
          : null;
      return parseClientOrgProfile(raw);
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

interface SetOrgProfileVars {
  organizationId: string;
  orgProfile: OrgProfile;
}

/**
 * Persist the org identity profile through the `set-org-profile` edge function,
 * the app's write path for `organizations.settings.org_profile`, gated on org-admin.
 * This is the agent-parity client path: the same write an agent would make.
 */
export function useSetOrgProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: SetOrgProfileVars): Promise<OrgProfile> => {
      const { data, error } = (await supabase.functions.invoke<{
        data: { orgProfile: OrgProfile };
      }>('set-org-profile', {
        body: { organizationId: vars.organizationId, orgProfile: vars.orgProfile },
      })) as { data: { data: { orgProfile: OrgProfile } } | null; error: unknown };
      if (error) {
        throw new Error(await getSupabaseFunctionError(error));
      }
      if (!data?.data?.orgProfile) {
        throw new Error('Saving the org profile returned no result.');
      }
      return data.data.orgProfile;
    },
    onSuccess: async (_profile, vars) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [ORG_PROFILE_QUERY_KEY, vars.organizationId],
        }),
        // The office type is read from the membership row, so the saved type reaches the
        // gates through this query.
        queryClient.invalidateQueries({ queryKey: ['user-organizations'] }),
      ]);
    },
  });
}
